import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, KeyRound } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function LoginPage({ role = "client" }) {
  const { t } = useLang();
  const { login, user } = useAuth();
  const { content } = useContent();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("magic");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = role === "admin";

  useEffect(() => {
    document.title = `${isAdmin ? t.login.adminTitle : t.login2.welcome} — Bezalel Visionary`;
    if (user && user.role) navigate(user.role === "admin" ? "/admin" : "/portal");
  }, [user]); // eslint-disable-line

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const sendMagic = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setBusy(true);
    try {
      await api.post("/auth/magic-link", { email, origin_url: window.location.origin });
      setInfo(t.login2.magicSent);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    setError(""); setInfo("");
    if (!email) { setError(t.contact.required); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setInfo(t.login2.resetSent);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#191713] flex items-center justify-center px-6 py-16" data-testid={`${role}-login-page`}>
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={content?.brand?.logo_dark || "/logo-dark.png"} alt="Bezalel Visionary" className="w-40 object-contain" data-testid="login-logo" />
        </div>
        <div className="bg-[#23201A] gold-keyline p-8 sm:p-10">
          <h1 className="font-serif text-2xl text-[#F7F3EC]" data-testid="login-title">
            {isAdmin ? t.login.adminTitle : t.login2.welcome}
          </h1>
          <p className="mt-2 text-sm text-[#9A9184]">{isAdmin ? t.login.adminSub : t.login2.subtitle}</p>

          {!isAdmin && (
            <>
              <button onClick={googleLogin} data-testid="login-google-button"
                className="mt-7 w-full flex items-center justify-center gap-3 bg-[#F7F3EC] text-[#191713] text-sm font-medium py-3.5 hover:bg-white transition-colors">
                <GoogleIcon /> {t.login2.google}
              </button>

              <div className="my-6 flex items-center gap-4 text-[0.6rem] tracking-[0.25em] uppercase text-[#9A9184]/60">
                <span className="h-px flex-1 bg-[#F7F3EC]/10" /> {t.login2.or} <span className="h-px flex-1 bg-[#F7F3EC]/10" />
              </div>
            </>
          )}

          <form onSubmit={mode === "magic" && !isAdmin ? sendMagic : passwordLogin} className="space-y-4">
            <div>
              <label className="field-label" htmlFor="login-email">{t.login.email}</label>
              <input id="login-email" type="email" data-testid="login-email-input" className="field-input-dark"
                placeholder={t.login2.emailPh} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            {(mode === "password" || isAdmin) && (
              <div>
                <div className="flex items-center justify-between">
                  <label className="field-label" htmlFor="login-password">{t.login.password}</label>
                  <button type="button" data-testid="login-forgot-button" onClick={forgot}
                    className="text-[0.68rem] text-[#B8933F] hover:text-[#C9A959] transition-colors mb-2">
                    {t.login2.forgot}
                  </button>
                </div>
                <input id="login-password" type="password" data-testid="login-password-input" className="field-input-dark"
                  value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
              </div>
            )}
            {error && <p className="text-sm text-red-400" role="alert" data-testid="login-error">{error}</p>}
            {info && <p className="text-sm text-emerald-400" data-testid="login-info">{info}</p>}
            <button type="submit" data-testid="login-submit-button" disabled={busy} className="btn-gold w-full disabled:opacity-60">
              {mode === "magic" && !isAdmin ? <><Mail size={15} /> {t.login2.magic}</> : (busy ? t.login.signingIn : t.login.submit)}
            </button>
          </form>

          {!isAdmin && (
            <button data-testid="login-toggle-mode"
              onClick={() => { setMode(mode === "magic" ? "password" : "magic"); setError(""); setInfo(""); }}
              className="mt-5 w-full flex items-center justify-center gap-2 text-xs text-[#9A9184] hover:text-[#F7F3EC] transition-colors">
              <KeyRound size={13} /> {mode === "magic" ? t.login2.usePassword : t.login2.useMagic}
            </button>
          )}
        </div>
        <Link to="/" data-testid="login-back-link" className="mt-8 flex items-center justify-center gap-2 text-sm text-[#9A9184] hover:text-[#B8933F] transition-colors">
          <ArrowLeft size={15} /> {t.login.back}
        </Link>
      </div>
    </main>
  );
}
